import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useUser } from "../../hooks/useUser";
import { usePost } from "../../hooks/usePost";
import PostHeader from "../Post/PostHeader";
import PostMedia from "../Components/PostMedia/PostMedia";
import PostActions from "../Components/PostActions/PostActions";
import PostContent from "../Post/PostContent";
import PollContent from "../PollContent/PollContent";
import PostContentShare from "../Components/PostContentShare/PostContentShare";
import PostChallenge from "../Components/PostChallenge/PostChallenge";
import { IoMdMore } from "react-icons/io";
import { MdModeEdit } from "react-icons/md";
import ReportDialog from "../Components/ReportDialog/ReportDialog";
import Toast from "../../components/Toast/Toast";
import "./ProfileView.scss";
import SkeletonLoader from "../Components/SkeletonLoader/SkeletonLoader";
import AddPost from "../AddPost/AddPost";
import qs from "qs";
import axios from "axios";
import UsersPanel from "../Components/UserListPanel/UsersPanel";
import { UsersService } from "../../services";
import { updateUser } from "../../redux/usersSlice";

const ProfileView = () => {
    const { userId } = useParams();
    const [profileUserId, setProfileUserId] = useState(userId);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pageRef = useRef(1);
    
    const currentUser = useSelector((state) => state.projectInfo.uid);
    const { users, handleUserFollowUnfollow } = useUser();
    const [showOptions, setShowOptions] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [isReporting, setIsReporting] = useState(false);
    const menuRef = useRef(null);
    const [shouldShowMoreBio, setShouldShowMoreBio] = useState(false);
    const bioRef = useRef(null);
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [editingPost, setEditingPost] = useState(null);
    const [showEditPopup, setShowEditPopup] = useState(false);

    const {
        loading,
        getUserPosts,
        likePost,
        togglePostComments,
        setPostMediaIndex,
        voteOnPoll,
        fetchCategories,
        activeMediaIndices = {},
        deletePost,
        updatePost,
        loadMoreUserPosts
    } = usePost();
    
    const postsState = useSelector((state) => state.posts.userPosts[profileUserId]);
    const [page, setPage] = useState(1);
    const userPosts = useMemo(() => postsState || {}, [postsState]);
    const categories = useSelector((state) => state.categories.entities) || [];
    const user = users[profileUserId];
    const projectInfo = useSelector((state) => state.projectInfo);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [userPanelOpen, setUserPanelOpen] = useState(false);
    const [actionType, setActionType] = useState("follow");
    const [followLoading, setFollowLoading] = useState(false);

    // Fetch user profile data once
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await UsersService.getUserProfile(userId);
                const userData = response.data.data;
                if (Number(userData.user_id) !== Number(userId)) {
                    setProfileUserId(userData.user_id);
                }
                dispatch(updateUser({ id: userData.user_id, changes: userData }));
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        };
        
        fetchUserData();
    }, [userId, dispatch]);

    // Infinite scroll handler - FIX: Use ref to avoid stale closure
    useEffect(() => {
        const handleScroll = () => {
            if (!hasMore || loadingMore) return;
            
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight;
            const clientHeight = window.innerHeight;
            
            if (scrollTop + clientHeight >= scrollHeight - 200) {
                setLoadingMore(true);
                const currentPostsState = postsState || {};
                const postsCount = Object.keys(currentPostsState).length;
                const nextPage = Math.ceil(postsCount / 10) + 1;
                
                loadMoreUserPosts(profileUserId, nextPage).then((response) => {
                    if (response && response.length > 0) {
                        setHasMore(response.length === 10);
                        setPage(nextPage);
                        pageRef.current = nextPage;
                    } else {
                        setHasMore(false);
                    }
                }).catch((error) => {
                    console.error("Error loading more posts:", error);
                }).finally(() => {
                    setLoadingMore(false);
                });
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasMore, loadingMore, userId, loadMoreUserPosts]);

    // Check if bio content is taller than 40px
    useEffect(() => {
        if (bioRef.current) {
            const bioHeight = bioRef.current.scrollHeight;
            setShouldShowMoreBio(bioHeight > 40);
        }
    }, [user?.bio]);

    // FIX: Fetch user data and posts once with proper checks
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch categories if not available
                if (!categories || categories.length === 0) {
                    await fetchCategories();
                }
                
                // Scroll to top
                window.scrollTo(0, 0);
                const container = document.querySelector(".community-content");
                if (container) {
                    container.scrollTop = 0;
                }
                
                // Fetch posts only if not already loaded for this user
                if (!postsState) {
                    await getUserPosts(profileUserId, 1);
                    setPage(2);
                } else {
                    setPage(Math.ceil(Object.keys(postsState).length / 10) + 1);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]); // Only re-run when userId changes - other deps are stable from hooks

    // Toast handling functions
    const hideToast = useCallback(() => {
        setToast(prev => ({ ...prev, show: false }));
    }, []);

    const showToastMessage = useCallback((message, type = "success") => {
        setToast({ show: true, message, type });
    }, []);

    // Post editing functions
    const handleEditPost = (post, postId) => {
        setEditingPost({ ...post, postId });
        setShowEditPopup(true);
    };

    const closeEditPopup = () => {
        setShowEditPopup(false);
        setEditingPost(null);
    };

    const handleEditSave = async (postId, updatedPostData) => {
        try {
            await updatePost(postId, updatedPostData);
            showToastMessage("Post updated successfully", "success");
            setShowEditPopup(false);
            setEditingPost(null);
            await getUserPosts(profileUserId);
        } catch (error) {
            console.error("Error updating post:", error);
            showToastMessage("Failed to update post", "error");
        }
    };

    const handleDeletePost = async (postId) => {
        try {
            await deletePost(postId);
            showToastMessage("Post deleted successfully", "success");
        } catch (error) {
            console.error("Error deleting post:", error);
            showToastMessage("Failed to delete post", "error");
            return Promise.reject(error);
        }
    };

    const onCarousalClick = (postId, mediaKeys, dir) => {
        const activeIndex = activeMediaIndices[postId] || 0;
        let newIndex;

        if (dir === "prev") {
            newIndex = Math.max(0, activeIndex - 1);
        } else {
            newIndex = Math.min(mediaKeys?.length - 1, activeIndex + 1);
        }

        setPostMediaIndex(postId, newIndex);
    };

    const onDisableComment = useCallback(async (postId) => {
        if (!userPosts[postId]) return;
        const currentStatus = userPosts[postId].comments_disabled;
        await togglePostComments(postId, currentStatus);
    }, [userPosts, togglePostComments]);

    const onCommentClick = useCallback(async (postId) => {
        const container = document.querySelector(".community-content");
        const scrollPosition = container?.scrollTop || 0;
        sessionStorage.setItem('ScrollPosition', scrollPosition);
        
        const sessionId = localStorage.getItem('sessionId');
        const { uid, pubid, dev_id, channelid, app_id, accessToken } = projectInfo;
        let ctimestamp = Date.now().toString();
        let ctime = ctimestamp.slice(0, 10);
        
        const data = {
            session_id: sessionId,
            user_id: uid,
            device_id: dev_id,
            publisherid: pubid,
            app_id: String(app_id),
            channel_id: channelid,
            event_type: "POP14",
            timestamp: ctime,
            post_id: String(postId),
        };
        
        const customConfig = {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Access-Control-Allow-Origin": true,
                crossorigin: true,
                "access-token": accessToken,
            },
        };
        
        try {
            const responseData = await axios.post(
                "https://analytics.poppo.tv/event",
                qs.stringify(data),
                customConfig
            );
        } catch (error) {
            console.error("Error logging event:", error);
        }
    }, [projectInfo]);

    const toggleLikePost = async (postId, isLiked) => {
        await likePost(postId, isLiked);
    };

    const handlePollVote = async (postId, updatedOptions) => {
        await voteOnPoll(postId, updatedOptions);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const toggleFollow = async () => {
        try {
            if (followLoading || users[profileUserId]?.is_following === undefined) return;
            setFollowLoading(true);
            const isCurrentlyFollowing = users[profileUserId]?.is_following;
            const result = await handleUserFollowUnfollow(profileUserId, currentUser, isCurrentlyFollowing);
            setFollowLoading(false);
            if (!result) {
                showToastMessage("Failed to update follow status", "error");
            }
        } catch (error) {
            setFollowLoading(false);
            console.error("Error updating follow status:", error);
            showToastMessage("Failed to update follow status", "error");
        }
    };

    const handleReportUser = async () => {
        setShowReportDialog(true);
    };

    const handleReportSubmit = useCallback(async (reason) => {
        try {
            const response = await UsersService.reportUser(profileUserId, reason);
            setShowReportDialog(false);
            setToast({
                show: true,
                message: "User reported successfully",
                type: "success"
            });
        } catch (err) {
            setShowReportDialog(false);
            if (err.response?.data?.alreadyReported) {
                setToast({
                    show: true,
                    message: "You have already reported this user.",
                    type: "warning"
                });
            } else {
                setToast({
                    show: true,
                    message: "Failed to report user. Please try again.",
                    type: "error"
                });
            }
        }
    }, [profileUserId]);

    if (!user) {
        return <div className="loading-container">Loading user data...</div>;
    }

    return (
        <div className="profile-view">
            <div className="user-profile">
                <div className="profile-header">
                    <div className="profile-pic-container">
                        <img
                            src={user?.user_image || "https://gizmeon.mdc.akamaized.net/PUB-50119/post/1749720612786.png"}
                            alt="Profile"
                            className="profile-pic"
                        />
                        {Number(profileUserId) === Number(currentUser) && (
                            <div
                                className="edit-icon-wrapper"
                                onClick={() => {
                                    const parsedUrl = new URL(window.location.href);
                                    const redirectedUrl = `${parsedUrl.origin}/account`;
                                    window.open(redirectedUrl, "_blank");
                                }}
                                title="Edit Profile"
                            >
                                <MdModeEdit className="edit-icon" />
                            </div>
                        )}
                    </div>

                    <div className="profile-info">
                        <div className="profile-name-actions">
                            <h2>{user?.user_name || "Unknown User"}</h2>
                        </div>

                        <div className="profile-stats">
                            <div className="stat-item-count" onClick={() => { setActionType("follow"); setUserPanelOpen(true); }}>
                                <span className="count">
                                    {users[profileUserId]?.followers_count}
                                </span>
                                <span className="label">Followers</span>
                            </div>
                            <div className="stat-item" onClick={() => { setActionType("following"); setUserPanelOpen(true); }}>
                                <span className="count">
                                    {users[profileUserId]?.following_count}
                                </span>
                                <span className="label">Following</span>
                            </div>
                        </div>
                        {Number(profileUserId) !== Number(currentUser) && (
                            <div className="profile-actions">
                                <button
                                    className={`follow-button ${users[profileUserId]?.is_following ? 'following' : ''}`}
                                    onClick={toggleFollow}
                                    disabled={followLoading}
                                >
                                    {followLoading ? 'Please wait...' : (users[profileUserId]?.is_following ? 'Following' : 'Follow')}
                                </button>
                                <div className="profile-more-menu" ref={menuRef}>
                                    <IoMdMore size={24} onClick={() => setShowOptions(!showOptions)} className="more-icon" />
                                    {showOptions && (
                                        <div className="options-menu">
                                            <button onClick={handleReportUser}>
                                                Report User
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="profile-posts">
                <h3 className="post-title">Posts</h3>
                {loading && Object.keys(userPosts).length === 0 ? (
                    <SkeletonLoader count={1} />
                ) : (postsState && Object.keys(userPosts).length === 0) ? (
                    <div className="no-posts">No posts yet</div>
                ) : (
                    <div className="instagram-container">
                        {Object.values(userPosts)
                            .slice()
                            .reverse()
                            .map((post) => {
                                const mediaKeys = post.media ? Object.keys(post.media) : [];
                                return (
                                    <div key={post.id} className="post">
                                        <PostHeader
                                            post={post}
                                            postId={post.id}
                                            onCommentDisable={onDisableComment}
                                            categories={categories}
                                            showToast={showToastMessage}
                                            onEditPost={handleEditPost}
                                            onDeletePost={handleDeletePost}
                                        />
                                        {mediaKeys.length > 0 && (
                                            <PostMedia
                                                post={post}
                                                postId={post.id}
                                                activeIndex={activeMediaIndices[post.id] || 0}
                                                onCarousalButtonClick={onCarousalClick}
                                            />
                                        )}
                                        {post.type === "CONTENT" && (
                                            <PostContentShare content={post?.data} type={post?.content_type} />
                                        )}
                                        {post.type === "CHALLENGE" && (
                                            <PostChallenge post={post} />
                                        )}
                                        {post.type === "POLL" && (
                                            <PollContent
                                                poll={post.data}
                                                postId={post.id}
                                                currentUser={currentUser}
                                                onVote={handlePollVote}
                                            />
                                        )}
                                        <div className="post-caption">
                                            <PostContent content={post.content} gif={post.gif_url} />
                                        </div>
                                        <PostActions
                                            comments={Number(post.comment_count) || 0}
                                            isLiked={post.user_has_liked}
                                            postId={post.id}
                                            toggleLikePost={toggleLikePost}
                                            isCommentsDisabled={post.comments_disabled}
                                            likesCount={post.like_count || 0}
                                            onCommentClick={onCommentClick}
                                            vanityUrl={post.vanity_url || ""}
                                            userId={profileUserId}
                                        />
                                    </div>
                                );
                            })}
                        {loadingMore && (
                            <div className="loading-more-container">
                                <SkeletonLoader count={1} />
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ReportDialog
                isOpen={showReportDialog}
                onClose={() => setShowReportDialog(false)}
                onSubmit={handleReportSubmit}
                isSubmitting={isReporting}
                type="user"
            />

            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={hideToast}
                    autoClose={3000}
                />
            )}

            {showEditPopup && (
                <AddPost
                    onClose={closeEditPopup}
                    isEditing={true}
                    editingPost={editingPost}
                    onEditSave={handleEditSave}
                />
            )}

            {userPanelOpen && (
                <UsersPanel
                    isOpen={userPanelOpen}
                    onClose={() => setUserPanelOpen(false)}
                    userId={profileUserId}
                    type={actionType}
                    userCount={actionType === "follow" ? Number(users[profileUserId]?.followers_count) : Number(users[profileUserId]?.following_count)}
                />
            )}
        </div>
    );
};

export default ProfileView;